import { useQueries, useQuery, UseQueryOptions } from '@tanstack/react-query';

import { getDerivativeKpisWithMetrics } from '@/features/projects/project-details/api/get-derivative-kpis-with-metrics';
import { getProjectKpis } from '@/features/projects/project-details/api/get-project-kpis';
import { DerivativeKpiWithMetrics, ProjectType } from '@/shared/types/api';

type ProjectKpisData = Awaited<ReturnType<typeof getProjectKpis>>;

interface UseProjectKpisOptions extends Omit<
  UseQueryOptions<ProjectKpisData>,
  'queryKey' | 'queryFn'
> {
  projectId: number;
  projectType: ProjectType;
}

export const projectKpisQueryKey = (
  projectId: number | undefined,
  projectType?: ProjectType | undefined,
) => ['projectKpis', projectId, projectType].filter(Boolean);

export const derivativeKpisQueryKey = (
  projectId: number | undefined,
  projectType?: ProjectType | undefined,
) => ['derivativeKpis', projectId, projectType].filter(Boolean);

export function useProjectKpis({
  projectId,
  projectType,
  ...options
}: UseProjectKpisOptions) {
  return useQuery({
    queryKey: projectKpisQueryKey(projectId, projectType),
    queryFn: () => {
      return getProjectKpis(projectId, projectType);
    },
    ...options,
  });
}

export interface UseProjectKpisWithMetricsOptions extends Omit<
  UseQueryOptions<DerivativeKpiWithMetrics[]>,
  'queryKey' | 'queryFn'
> {
  projectId: number | undefined;
  projectType: ProjectType | undefined;
}

export const useProjectKpisWithMetrics = ({
  projectId,
  projectType,
  ...options
}: UseProjectKpisWithMetricsOptions) => {
  const isEnabled =
    options.enabled !== false && projectId != null && projectType != null;

  return useQueries({
    queries: [
      {
        queryKey: projectKpisQueryKey(projectId, projectType),
        queryFn: () => getProjectKpis(projectId!, projectType!),
        enabled: isEnabled,
      },
      {
        queryKey: derivativeKpisQueryKey(projectId, projectType),
        queryFn: () => getDerivativeKpisWithMetrics(projectId!),
        enabled: isEnabled,
      },
    ],
    ...options,
  });
};
